import type { CalculatedAttributesCollector } from '../../calculatedAttributes/collect';
import {
  NO_VERTICAL,
  VERTICAL_BY_MODE,
} from '../../calculatedAttributes/moduleMode';
import type { TravelTripCriteria } from '../../types/travelInsurance';
import { QuickAnswerProps } from './types';

/**
 * The trip this module is authored for, or `undefined` when it is not a Travel
 * Insurance module. Mirrors `getTableBuilderTripCriteria`: the criteria have one
 * reader per module, and the collector below is its only consumer today.
 */
export const getQuickAnswerTripCriteria = (
  component: QuickAnswerProps,
): TravelTripCriteria | undefined => {
  if ((component.mode ?? 'GENERAL') !== 'TRAVEL_INSURANCE') return undefined;
  return {
    insuranceType: component.defaultInsuranceType,
    travelAsFamily: component.defaultTravelAsFamily,
    destination: component.defaultDestination,
    region: component.defaultRegion,
    endDateOffsetDays: component.endDateOffsetDays,
  };
};

/**
 * QuickAnswer's implementation of the calculated-attributes collection contract
 * (CMS-1956, CMS-1928): the module reports its own trip or loan, its products
 * and the attributes it draws, keyed by its own id. The page's Travel Policy
 * Search and loan calculator are irrelevant to it, exactly as for Table Builder.
 */
export const collectQuickAnswerCalculatedAttributes: CalculatedAttributesCollector<
  QuickAnswerProps
> = (component) => {
  // Summary draws no products, so there is nothing to calculate.
  if (component.displayMode === 'Summary') return undefined;

  const mode = component.mode ?? 'GENERAL';
  const vertical = VERTICAL_BY_MODE[mode];
  // The vertical is reported even with nothing to calculate: the module's
  // apply CTAs run under it.
  if (vertical === NO_VERTICAL) return { vertical, groups: [] };

  const trip = getQuickAnswerTripCriteria(component);
  if (mode === 'TRAVEL_INSURANCE' && !trip) return { vertical, groups: [] };
  // Optional in Strapi (CMS-1928): without both, the derived values are "-".
  if (
    mode === 'PERSONAL_LOAN' &&
    !(component.loanAmount && component.loanTenure)
  )
    return { vertical, groups: [] };

  // A mapping defect, not a configuration: the id is what keys the result back
  // to this instance. Skipping silently would leave a module with no values and
  // no explanation.
  if (!component.id) {
    console.error(
      '[collectQuickAnswerCalculatedAttributes] Quick Answer without an id — its calculation cannot be requested',
    );
    return undefined;
  }

  return {
    vertical,
    groups: [
      {
        productIds: (component.products ?? [])
          .map((product) => product?.title)
          .filter((title): title is string => !!title),
        attributeNames: (component.attributes ?? []).map(
          (attribute) => attribute?.attribute?.name,
        ),
      },
    ],
    trip,
    loanFields:
      mode === 'PERSONAL_LOAN'
        ? { amount: component.loanAmount, tenure: component.loanTenure }
        : undefined,
  };
};
